import { useState, useEffect, useRef, useCallback } from 'react';
import { ENDPOINTS } from '../api/api';

function toTitleCase(str = '') {
  return str.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

function slugify(name) {
  return name.toLowerCase().replace(/\s+/g, '-');
}

const IMEI_RE = /^\d{14,15}$/;
const ZONE_RE = /^\d{4,13}$/;

export function useGlobalSearch(navigate) {
  const [query, setQuery]       = useState('');
  const [results, setResults]   = useState([]);
  const [open, setOpen]         = useState(false);
  const [searching, setSearching] = useState(false);
  const [navigating, setNavigating] = useState(false);

  const districtsRef  = useRef(null);
  const loadingRef    = useRef(false);
  const debounceRef   = useRef(null);

  const ensureDistricts = useCallback(async () => {
    if (districtsRef.current !== null) return districtsRef.current;
    if (loadingRef.current) return [];
    loadingRef.current = true;
    try {
      const res  = await fetch(ENDPOINTS.summary);
      const json = await res.json();
      const list = Object.keys(json.devicesPerRegion ?? {}).map((name) => {
        const label = toTitleCase(name);
        return { type: 'district', label, path: `/district/${slugify(label)}` };
      });
      districtsRef.current = list;
      return list;
    } catch {
      districtsRef.current = [];
      return [];
    } finally {
      loadingRef.current = false;
    }
  }, []);

  // Pre-load districts when user focuses search
  const onFocus = useCallback(() => {
    setOpen(true);
    ensureDistricts();
  }, [ensureDistricts]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    const q = query.trim();

    if (!q) {
      setResults([]);
      setSearching(false);
      return;
    }

    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      const list = await ensureDistricts();
      const lower = q.toLowerCase();

      const districtMatches = list.filter((d) =>
        d.label.toLowerCase().includes(lower)
      );

      const imeiResult = IMEI_RE.test(q)
        ? [{ type: 'device', label: q, imei: q }]
        : [];

      let schoolResult = [];
      if (ZONE_RE.test(q)) {
        try {
          const res  = await fetch(ENDPOINTS.school(q));
          if (res.ok) {
            const json = await res.json();
            if (json.name) {
              const districtSlug = slugify(toTitleCase(json.region   || 'unknown'));
              const circuitSlug  = slugify(toTitleCase(json.province || 'unknown'));
              schoolResult = [{ type: 'school', label: json.name, zoneID: q, districtSlug, circuitSlug }];
            }
          }
        } catch { /* ignore */ }
      }

      setResults([...districtMatches, ...imeiResult, ...schoolResult]);
      setSearching(false);
    }, 280);

    return () => clearTimeout(debounceRef.current);
  }, [query, ensureDistricts]);

  const handleSelect = useCallback(async (result) => {
    setOpen(false);
    setQuery('');
    setResults([]);

    if (result.type === 'district') {
      navigate(result.path);
      return;
    }

    if (result.type === 'school') {
      navigate(`/district/${result.districtSlug}/circuit/${result.circuitSlug}/school/${result.zoneID}`);
      return;
    }

    if (result.type === 'device') {
      setNavigating(true);
      try {
        const res  = await fetch(ENDPOINTS.device(result.imei));
        const json = await res.json();
        const d    = json.device;
        if (d) {
          const districtSlug = slugify(toTitleCase(d.region   || 'unknown'));
          const circuitSlug  = slugify(toTitleCase(d.province || 'unknown'));
          navigate(`/district/${districtSlug}/circuit/${circuitSlug}/device/${result.imei}`);
        }
      } catch {
        // navigate with placeholder slugs so the device page can still load
        navigate(`/district/unknown/circuit/unknown/device/${result.imei}`);
      } finally {
        setNavigating(false);
      }
    }
  }, [navigate]);

  return { query, setQuery, results, open, setOpen, onFocus, searching, navigating, handleSelect };
}
